import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Palette, PenTool, Layers, LayoutGrid, Sparkles, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const designServices = [
  {
    icon: PenTool,
    title: 'Graphic Design',
    description: 'Custom invitations, event signage, programs, menus, and branded materials that set the tone for your celebration.',
    items: ['Save the Dates & Invitations', 'Event Programs & Menus', 'Signage & Wayfinding', 'Place Cards & Seating Charts']
  },
  {
    icon: Palette,
    title: 'Content Creation',
    description: 'Visual content designed to capture the essence of your event—from social media graphics to digital displays.',
    items: ['Social Media Graphics', 'Digital Invitations', 'Event Announcements', 'Thank You Cards']
  },
  {
    icon: Layers,
    title: 'Drapery Design',
    description: 'Elegant fabric treatments and drapery concepts that transform spaces and create atmosphere.',
    items: ['Ceiling Treatments', 'Wall Drapery', 'Entrance Designs', 'Backdrop Concepts']
  },
  {
    icon: LayoutGrid,
    title: 'Floor Plan Execution',
    description: 'Strategic layouts optimized for guest flow, functionality, and visual impact. Available under select circumstances.',
    items: ['Space Planning', 'Guest Flow Optimization', 'Seating Arrangements', 'Vendor Placement']
  }
];

const rentalItems = [
  { name: 'Crystal Chandeliers', image: 'https://images.unsplash.com/photo-1633443682042-17462ad4ad76?q=85&w=400&auto=format&fit=crop' },
  { name: 'Statement Lighting', image: 'https://images.unsplash.com/photo-1718463383973-5db9edea7d34?q=85&w=400&auto=format&fit=crop' },
  { name: 'Luxury Furniture', image: 'https://images.unsplash.com/photo-1629943194535-8e605f26a19c?q=85&w=400&auto=format&fit=crop' },
  { name: 'Decorative Accents', image: 'https://images.pexels.com/photos/36230868/pexels-photo-36230868.jpeg?auto=compress&cs=tinysrgb&w=400' },
];

export const Design = () => {
  return (
    <div className="min-h-screen pt-24" data-testid="design-page">
      <SEO
        title="Event Design Services | White Glove Event Concierge"
        description="Custom event design in Nashville: invitations, signage, programs, menus, and branded materials that set the tone for high-value celebrations."
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Design Services</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
                Visual Excellence<br />
                <span className="text-gold-gradient">For Every Detail</span>
              </h1>
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                From graphic design to drapery concepts, we create the visual elements that make your event truly memorable. Every piece is crafted to reflect your unique style and vision.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                Plus, elevate your space with our high-end decor rentals—crystal chandeliers, statement pieces, and luxury accents available exclusively for your event.
              </p>
              <Link to="/contact" className="btn-primary" data-testid="design-hero-cta">
                Discuss Your Vision
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.pexels.com/photos/36230868/pexels-photo-36230868.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Event Design"
                className="w-full h-[500px] object-cover rounded-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="design-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">What We Create</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mt-4">
              Design Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {designServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-dark p-8 rounded-sm"
                data-testid={`design-service-${index}`}
              >
                <service.icon className="w-10 h-10 text-[#D4AF37] mb-6" />
                <h3 className="font-heading text-2xl text-white mb-3">{service.title}</h3>
                <p className="text-white/60 leading-relaxed mb-6">{service.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {service.items.map((item) => (
                    <span key={item} className="text-white/50 text-sm flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#D4AF37] rounded-full" />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Decor Rentals */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="decor-rentals">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Exclusive Rentals</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mt-4">
              High-End Decor
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl mx-auto">
              Elevate your event with our curated collection of luxury decor items available for rent. From stunning chandeliers to statement pieces that transform any space.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {rentalItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-sm"
                data-testid={`rental-item-${index}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-heading text-lg text-white">{item.name}</h3>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <div className="inline-flex items-center gap-3 text-[#D4AF37]">
              <Sparkles size={20} />
              <span className="text-sm uppercase tracking-widest">Full inventory available upon consultation</span>
              <Sparkles size={20} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Corporate Events Note */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="flex items-start gap-6">
            <div className="w-12 h-12 flex-shrink-0 border border-[#D4AF37] rounded-full flex items-center justify-center">
              <LayoutGrid className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-heading text-xl text-white mb-2">Corporate Event Coordination</h3>
              <p className="text-white/60 leading-relaxed">
                Under select circumstances, we provide full coordination services for corporate events—product launches, galas, conferences, and more. Contact us to discuss your corporate needs.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <span className="font-accent text-2xl text-[#D4AF37] italic">Let&apos;s create together</span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mt-4 mb-6">
              Ready to Design Your Event?
            </h2>
            <p className="text-white/60 mb-8">
              Share your vision with us—from custom graphics to decor rentals, we&apos;ll help you create a cohesive and stunning event aesthetic.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="design-cta">
              Start Your Project <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
